/** Which rendition of the film to start with: the smallest that fills the screen, capped by what the connection suggests. */

type Connection = { saveData?: boolean; effectiveType?: string; downlink?: number }

export type QualityInput = {
  /** CSS px width of the viewport. */
  viewportWidth: number
  devicePixelRatio: number
  connection?: Connection
}

/** The highest rung (height) the connection can sustain; undefined data means no cap. */
export function connectionCap(c: Connection | undefined, rungs: readonly number[]): number {
  const top = rungs[0] ?? 360
  const lowest = rungs[rungs.length - 1] ?? 360
  if (!c) return top
  let cap = top
  if (c.saveData) cap = lowest
  else if (c.effectiveType === "slow-2g" || c.effectiveType === "2g") cap = lowest
  else if (c.effectiveType === "3g") cap = 480
  else if (typeof c.downlink === "number" && c.downlink > 0) {
    // The files average about 1.35 Mbps (1080p), 0.72 (720p) and 0.40 (480p); ask for about 1.2x that.
    // Chrome's downlink is a coarse estimate that starts low, so a wider margin cost a rung on most connections.
    cap = c.downlink < 0.9 ? 480 : c.downlink < 1.6 ? 720 : top
  }
  return Math.max(Math.min(cap, top), lowest)
}

/** Rung heights are listed highest first. Returns the index to start from. */
export function pickRungIndex(input: QualityInput, rungs: readonly number[]): number {
  // The dialog is at most 1080 px wide; 16:9 gives the height the picture needs at the device's pixel density.
  const shown = Math.min(input.viewportWidth - 32, 1080)
  const neededHeight = ((shown * 9) / 16) * Math.min(Math.max(input.devicePixelRatio, 1), 2) * 0.9
  // The smallest rendition that still fills the screen (the highest one when none does).
  let bySize = 0
  for (let i = rungs.length - 1; i >= 0; i--) {
    if ((rungs[i] ?? 0) >= neededHeight) {
      bySize = i
      break
    }
  }
  const cap = connectionCap(input.connection, rungs)
  const capIndex = rungs.findIndex((h) => h <= cap)
  return Math.max(bySize, capIndex === -1 ? rungs.length - 1 : capIndex)
}
